import { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner, Alert } from "react-bootstrap";
import PropTypes from "prop-types";

/**
 * Modal reutilizável para renderizar formulários dinâmicos baseados em eventos.
 * 
 * Props:
 * - show: boolean - Controla visibilidade
 * - onHide: function - Callback ao fechar (cancelar)
 * - formulario: object - Dados do formulário (retornado por buscarFormularioEvento)
 * - onSuccess: function(respostas) - Callback com as respostas ao confirmar
 * - loading: boolean - Estado de loading externo (opcional)
 */
const FormularioEventoModal = ({ show, onHide, formulario, onSuccess, loading = false }) => {
    const [valores, setValores] = useState({});
    const [arquivos, setArquivos] = useState({});
    const [erros, setErros] = useState({});
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (show && formulario) {
            const valoresIniciais = {};
            (formulario.campos || []).forEach((campo) => {
                if (campo.tipo === "checkbox") {
                    valoresIniciais[campo.slug] = [];
                } else {
                    valoresIniciais[campo.slug] = "";
                }
            });
            setValores(valoresIniciais);
            setArquivos({});
            setErros({});
        }
    }, [show, formulario]);

    const handleChange = (slug, value) => {
        setValores((prev) => ({
            ...prev,
            [slug]: value,
        }));
        // Limpar erro do campo ao editar
        if (erros[slug]) {
            setErros((prev) => {
                const newErros = { ...prev };
                delete newErros[slug];
                return newErros;
            });
        }
    };

    const handleCheckboxChange = (slug, opcao, checked) => {
        setValores((prev) => {
            const atuais = prev[slug] || [];
            if (checked) {
                return { ...prev, [slug]: [...atuais, opcao] };
            } else {
                return { ...prev, [slug]: atuais.filter((o) => o !== opcao) };
            }
        });
    };

    const handleFileChange = (slug, file) => {
        setArquivos((prev) => ({
            ...prev,
            [slug]: file,
        }));
    };

    const validar = () => {
        const novosErros = {};
        
        (formulario?.campos || []).forEach((campo) => {
            if (campo.obrigatorio) {
                const valor = valores[campo.slug];
                const arquivo = arquivos[campo.slug];

                if (campo.tipo === "file") {
                    if (!arquivo) {
                        novosErros[campo.slug] = "Este campo é obrigatório";
                    }
                } else if (campo.tipo === "checkbox") {
                    if (!valor || valor.length === 0) {
                        novosErros[campo.slug] = "Selecione pelo menos uma opção";
                    }
                } else {
                    if (!valor || (typeof valor === "string" && !valor.trim())) {
                        novosErros[campo.slug] = "Este campo é obrigatório";
                    }
                }
            }
        });

        setErros(novosErros);
        return Object.keys(novosErros).length === 0;
    };

    const handleSubmit = async () => {
        if (!validar()) {
            return;
        }

        setSubmitting(true);

        try {
            const valoresEstruturados = [];
            (formulario.campos || []).forEach(campo => {
                const valor = valores[campo.slug];
                if (valor !== undefined && valor !== null && valor !== "") {
                     valoresEstruturados.push({
                        slug: campo.slug,
                        label: campo.label,
                        value: valor,
                        tipo: campo.tipo
                    });
                }
            });

            const respostas = {
                formulario_id: formulario.id,
                formulario_nome: formulario.nome,
                valores: valoresEstruturados,
            };

            await onSuccess(respostas, arquivos);
        } catch (error) {
            console.error("[FormularioEventoModal] Erro ao submeter:", error);
        } finally {
            setSubmitting(false);
        }
    };

    if (!formulario) {
        return null;
    }

    const campos = formulario.campos || [];
    const titulo = formulario.titulo_modal || formulario.nome || "Preencha o Formulário";

    return (
        <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>{titulo}</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
                {formulario.descricao && (
                    <Alert variant="info" className="mb-3">
                        {formulario.descricao}
                    </Alert>
                )}

                {campos.length === 0 ? (
                    <Alert variant="warning">
                        Este formulário não possui campos configurados.
                    </Alert>
                ) : (
                    campos.map((campo) => (
                        <Form.Group className="mb-3" key={campo.slug}>
                            <Form.Label>
                                {campo.label}
                                {campo.obrigatorio && <span className="text-danger"> *</span>}
                            </Form.Label>

                            {/* TEXT */}
                            {campo.tipo === "text" && (
                                <Form.Control
                                    type="text"
                                    placeholder={campo.placeholder || ""}
                                    value={valores[campo.slug] || ""}
                                    onChange={(e) => handleChange(campo.slug, e.target.value)}
                                    isInvalid={!!erros[campo.slug]}
                                    disabled={loading || submitting}
                                />
                            )}

                            {/* TEXTAREA */}
                            {campo.tipo === "textarea" && (
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder={campo.placeholder || ""}
                                    value={valores[campo.slug] || ""}
                                    onChange={(e) => handleChange(campo.slug, e.target.value)}
                                    isInvalid={!!erros[campo.slug]}
                                    disabled={loading || submitting}
                                />
                            )}

                            {/* DATE */}
                            {campo.tipo === "date" && (
                                <Form.Control
                                    type="date"
                                    value={valores[campo.slug] || ""}
                                    onChange={(e) => handleChange(campo.slug, e.target.value)}
                                    isInvalid={!!erros[campo.slug]}
                                    disabled={loading || submitting}
                                />
                            )}

                            {/* FILE */}
                            {campo.tipo === "file" && (
                                <Form.Control
                                    type="file"
                                    accept={campo.extensoes || ".pdf,.jpg,.jpeg,.png"}
                                    onChange={(e) => handleFileChange(campo.slug, e.target.files[0])}
                                    isInvalid={!!erros[campo.slug]}
                                    disabled={loading || submitting}
                                />
                            )}

                            {/* SELECT */}
                            {campo.tipo === "select" && (
                                <Form.Select
                                    value={valores[campo.slug] || ""}
                                    onChange={(e) => handleChange(campo.slug, e.target.value)}
                                    isInvalid={!!erros[campo.slug]}
                                    disabled={loading || submitting}
                                >
                                    <option value="">Selecione...</option>
                                    {(campo.opcoes || []).map((opcao, i) => (
                                        <option key={i} value={opcao}>
                                            {opcao}
                                        </option>
                                    ))}
                                </Form.Select>
                            )}

                            {/* RADIO */}
                            {campo.tipo === "radio" && (
                                <div>
                                    {(campo.opcoes || []).map((opcao, i) => (
                                        <Form.Check
                                            key={i}
                                            type="radio"
                                            name={campo.slug}
                                            label={opcao}
                                            value={opcao}
                                            checked={valores[campo.slug] === opcao}
                                            onChange={(e) => handleChange(campo.slug, e.target.value)}
                                            isInvalid={!!erros[campo.slug]}
                                            disabled={loading || submitting}
                                        />
                                    ))}
                                </div>
                            )}

                            {/* CHECKBOX */}
                            {campo.tipo === "checkbox" && (
                                <div>
                                    {(campo.opcoes || []).map((opcao, i) => (
                                        <Form.Check
                                            key={i}
                                            type="checkbox"
                                            label={opcao}
                                            checked={(valores[campo.slug] || []).includes(opcao)}
                                            onChange={(e) =>
                                                handleCheckboxChange(campo.slug, opcao, e.target.checked)
                                            }
                                            isInvalid={!!erros[campo.slug]}
                                            disabled={loading || submitting}
                                        />
                                    ))}
                                </div>
                            )}

                            {/* Observação / Texto de ajuda */}
                            {campo.observacao && (
                                <Form.Text className="text-muted">{campo.observacao}</Form.Text>
                            )}

                            {/* Erro */}
                            {erros[campo.slug] && (
                                <Form.Control.Feedback type="invalid" style={{ display: "block" }}>
                                    {erros[campo.slug]}
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    ))
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="danger" onClick={onHide} disabled={loading || submitting}>
                    Cancelar
                </Button>
                <Button
                    variant="success"
                    onClick={handleSubmit}
                    disabled={loading || submitting || campos.length === 0}
                >
                    {submitting ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Processando...
                        </>
                    ) : (
                        "Confirmar"
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

FormularioEventoModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    formulario: PropTypes.shape({
        id: PropTypes.number,
        nome: PropTypes.string,
        titulo_modal: PropTypes.string,
        descricao: PropTypes.string,
        campos: PropTypes.arrayOf(
            PropTypes.shape({
                slug: PropTypes.string.isRequired,
                label: PropTypes.string.isRequired,
                tipo: PropTypes.string.isRequired,
                obrigatorio: PropTypes.bool,
                opcoes: PropTypes.array,
                placeholder: PropTypes.string,
                observacao: PropTypes.string,
                extensoes: PropTypes.string,
            })
        ),
    }),
    onSuccess: PropTypes.func.isRequired,
    loading: PropTypes.bool,
};

export default FormularioEventoModal;
